import type { Logger } from "@expert/logging";

import ky, { HTTPError } from "ky";

import type { DefaultPayload } from "./types";

import { defaultHeaders, defaultTimeoutInMS } from "../types";

/** No MDN: the line comes from `token`, the one `verifyVerizonCustomer` returned once the PIN was verified. */
type PrepareVerizonReplacementPayload = DefaultPayload & {
    token: string;
};

/**
 * Why the backend found no Verizon replacement device it can activate. `LINE_MISMATCH` means the shipped
 * claim is for a different line on the account than the verified MDN; like AT&T, only the verified line
 * is activated. Every code routes to the guide's `noEligibleClaim` step, the manual flow in ACSS.
 */
export type VerizonIneligibleCode =
    | "NO_ELIGIBLE_CLAIM"
    | "AMBIGUOUS_CLAIMS"
    | "MISSING_DEVICE_DETAILS"
    | "LINE_MISMATCH";

/**
 * The replacement device as described to the expert. The backend never returns the IMEI, ICCID or the
 * full line: they stay inside `token`, which `activateDevice` sends back.
 */
export interface VerizonReplacementDevice {
    make: string;
    model: string;
    eSim: boolean;
    lineLast4: string;
    claimDate: string;
}

export type PrepareVerizonReplacementResult =
    | ({ eligible: true; token: string } & VerizonReplacementDevice)
    | { eligible: false; code?: VerizonIneligibleCode; errorCode?: string };

/**
 * Looks up the Verizon replacement device for the line the customer verified with their PIN. A request that
 * fails (403 when the expert isn't enabled or the verification token is invalid, 400, 500, network)
 * resolves to `eligible: false` with no `code`, so the guide falls back to the manual flow.
 */
export const prepareVerizonReplacement = async (
    ssoAccessToken: string,
    payload: PrepareVerizonReplacementPayload,
    logger: Logger,
    apiBaseUrl: string,
): Promise<PrepareVerizonReplacementResult> => {
    try {
        const url = `${apiBaseUrl}/api/device-activation/v1/verizon/replacement`;
        const resp = await ky.post(url, {
            headers: {
                ...defaultHeaders,
                Authorization: `Bearer ${ssoAccessToken}`,
            },
            json: payload,
            timeout: defaultTimeoutInMS,
        });

        return await resp.json<PrepareVerizonReplacementResult>();
    } catch (error) {
        logger.warn({ error }, "Solve Guide | Error: Could not look up the Verizon replacement device.");

        let errorCode = "";
        if (error instanceof HTTPError) {
            try {
                const errorJson = await error.response.json<{ code?: string }>();
                errorCode = errorJson.code ?? "";
            } catch {
                errorCode = "";
            }
        }

        return { eligible: false, errorCode };
    }
};
